import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';
import PageTransition from '../components/PageTransition';
import { formatCurrency, formatCurrencyCompact } from '../utils/currency';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';
import { useAuth } from '../context/AuthContext';

// ── OptiKa palette ────────────────────────────────────────────────────────────

const C = {
  ink:     '#0B0E17',
  surface: '#1E2235',
  border:  '#2A2E45',
  muted:   '#8892A4',
  gold:    '#F0B429',
  teal:    '#00C9B1',
  coral:   '#FF6B6B',
  lavender:'#A78BFA',
  sky:     '#38BDF8',
  text:    '#E8EDF5',
  sub:     '#C5CDD8',
};

const TABS = [
  { key: 'assistant',  label: '🤖 AI Assistant' },
  { key: 'forecast',   label: '📈 Forecasting' },
  { key: 'anomalies',  label: '🔍 Anomaly Detection' },
  { key: 'health',     label: '💚 Business Health' },
];

// ── CSS ───────────────────────────────────────────────────────────────────────

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Instrument+Serif:ital@1&display=swap');

  .ai-page * { box-sizing: border-box; }

  .ai-page {
    font-family: 'Cabinet Grotesk', 'Inter', system-ui, sans-serif;
    background: ${C.ink};
    min-height: 100vh;
    color: ${C.text};
  }

  .ai-mono { font-family: 'JetBrains Mono', monospace; }
  .ai-serif { font-family: 'Instrument Serif', serif; font-style: italic; }

  .ai-card {
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 14px;
    padding: 22px;
  }

  .ai-tab-btn {
    padding: 12px 20px; font-size: 13px; font-weight: 600;
    cursor: pointer; background: transparent; border: none;
    border-bottom: 3px solid transparent; margin-bottom: -2px;
    transition: all 0.2s; outline: none; white-space: nowrap;
    color: ${C.muted};
    font-family: 'Cabinet Grotesk', sans-serif;
  }
  .ai-tab-btn.active { color: ${C.gold}; border-bottom-color: ${C.gold}; background: rgba(240,180,41,0.06); border-radius: 10px 10px 0 0; }
  .ai-tab-btn:hover:not(.active) { color: ${C.text}; }

  .ai-badge {
    display: inline-block; padding: 3px 10px; border-radius: 100px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.02em;
  }

  .ai-skeleton {
    border-radius: 10px;
    background: linear-gradient(90deg, ${C.surface} 25%, #252A3D 50%, ${C.surface} 75%);
    background-size: 200% 100%;
    animation: ai-shimmer 1.4s infinite;
  }

  .ai-chat-area {
    padding: 18px 18px 0;
    max-height: 420px;
    overflow-y: auto;
    scroll-behavior: smooth;
  }
  .ai-chat-area::-webkit-scrollbar { width: 4px; }
  .ai-chat-area::-webkit-scrollbar-track { background: transparent; }
  .ai-chat-area::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 4px; }

  .ai-input {
    flex: 1; padding: 9px 14px; border-radius: 10px;
    border: 1.5px solid ${C.border}; font-size: 13px;
    background: rgba(255,255,255,0.04); color: ${C.text};
    outline: none; transition: border-color 0.2s;
    font-family: 'Cabinet Grotesk', sans-serif;
  }
  .ai-input:focus { border-color: ${C.gold}; }
  .ai-input::placeholder { color: ${C.muted}; }

  .ai-send-btn {
    background: ${C.gold}; color: #000; border: none; border-radius: 10px;
    padding: 9px 18px; font-size: 13px; font-weight: 700;
    cursor: pointer; white-space: nowrap; transition: background 0.18s;
    font-family: 'Cabinet Grotesk', sans-serif;
  }
  .ai-send-btn:disabled { background: #3A3F55; color: ${C.muted}; cursor: not-allowed; }
  .ai-send-btn:not(:disabled):hover { background: #E0A520; }

  .ai-quick-btn {
    padding: 4px 11px; border-radius: 100px;
    border: 1px solid ${C.border}; background: transparent;
    font-size: 11px; color: ${C.muted}; cursor: pointer;
    transition: all 0.15s; font-family: 'Cabinet Grotesk', sans-serif;
  }
  .ai-quick-btn:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .ai-quick-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .ai-refresh-btn {
    padding: 6px 14px; border-radius: 8px; border: 1px solid ${C.border};
    background: transparent; font-size: 12px; color: ${C.muted};
    cursor: pointer; transition: all 0.15s;
    font-family: 'Cabinet Grotesk', sans-serif;
  }
  .ai-refresh-btn:hover { border-color: ${C.gold}; color: ${C.gold}; }

  .ai-anomaly-card {
    border-radius: 12px; border: 1px solid;
    padding: 16px 20px; display: flex; gap: 14px; align-items: flex-start;
  }

  .ai-bar-track {
    height: 7px; border-radius: 100px;
    background: rgba(255,255,255,0.07); overflow: hidden; margin-top: 6px;
  }
  .ai-bar-fill { height: 100%; border-radius: 100px; transition: width 1s ease; }

  .ai-stat-mini {
    background: rgba(255,255,255,0.05);
    border-radius: 10px; padding: 10px 14px;
  }

  .ai-rec-card {
    border-radius: 12px; border: 1px solid; padding: 14px 16px;
  }

  @keyframes ai-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  @keyframes ai-bounce  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
  @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes ai-fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
`;

// ── Shared primitives ─────────────────────────────────────────────────────────

function Skeleton({ h = 16, w = '100%', r = 8 }) {
  return <div className="ai-skeleton" style={{ height: h, width: w, borderRadius: r }} />;
}

function Card({ children, style }) {
  return <div className="ai-card" style={style}>{children}</div>;
}

function Badge({ children, color, bg, style }) {
  return (
    <span className="ai-badge" style={{ background: bg || 'rgba(255,255,255,0.08)', color: color || C.muted, ...style }}>
      {children}
    </span>
  );
}

function SpinIcon({ size = 20, color = C.gold }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

// ── Animated score circle ─────────────────────────────────────────────────────

function ScoreCircle({ score, size = 140 }) {
  const animated = useAnimatedCounter(score, 1200);
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (animated / 100) * circumference;
  const color = score === 0 ? C.muted : score >= 90 ? C.teal : score >= 76 ? '#16A34A' : score >= 61 ? C.sky : score >= 46 ? C.gold : C.coral;

  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={C.border} strokeWidth={10} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={color} strokeWidth={10}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1.2s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span className="ai-mono" style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{Math.round(animated)}</span>
        <span style={{ fontSize: 11, color: C.muted }}>/ 100</span>
      </div>
    </div>
  );
}

// ── Dimension bar ─────────────────────────────────────────────────────────────

function DimBar({ label, score }) {
  const [width, setWidth] = useState(0);
  useEffect(() => { setTimeout(() => setWidth(score), 100); }, [score]);
  const color = score >= 75 ? C.teal : score >= 50 ? C.gold : C.coral;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
        <span className="ai-mono" style={{ fontSize: 12, fontWeight: 700, color }}>{score}</span>
      </div>
      <div className="ai-bar-track">
        <div className="ai-bar-fill" style={{ width: `${width}%`, background: color }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — AI ASSISTANT
// ─────────────────────────────────────────────────────────────────────────────

function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '4px 0' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: 7, height: 7, borderRadius: '50%', background: C.muted,
          animation: `ai-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
        }} />
      ))}
    </div>
  );
}

function ChatBubble({ role, content, userName }) {
  const isUser = role === 'user';
  const initials = userName ? userName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : 'U';

  function renderContent(text) {
    return text.split('\n').map((line, i) => {
      const parts = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      const isBullet = line.match(/^[-•]\s/);
      return (
        <div key={i} style={{ marginBottom: i < text.split('\n').length - 1 ? 3 : 0, paddingLeft: isBullet ? 8 : 0 }}
          dangerouslySetInnerHTML={{ __html: isBullet ? `• ${parts.slice(2)}` : parts }} />
      );
    });
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexDirection: isUser ? 'row-reverse' : 'row', marginBottom: 14 }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        background: isUser ? C.gold : 'rgba(0,201,177,0.2)',
        border: isUser ? 'none' : `1px solid ${C.teal}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 800,
        color: isUser ? '#000' : C.teal,
        fontFamily: 'JetBrains Mono, monospace',
      }}>
        {isUser ? initials : 'AI'}
      </div>
      <div style={{
        maxWidth: '72%',
        background: isUser ? 'rgba(240,180,41,0.15)' : 'rgba(255,255,255,0.05)',
        border: `1px solid ${isUser ? 'rgba(240,180,41,0.3)' : C.border}`,
        color: isUser ? C.gold : C.text,
        borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        padding: '10px 14px',
        fontSize: 13,
        lineHeight: 1.55,
      }}>
        {renderContent(content)}
      </div>
    </div>
  );
}

const QUICK_QUESTIONS = [
  'Net profit margin?',
  'Top customers?',
  'Expiring items?',
  'Outstanding debts?',
  'Best selling product?',
  'Restock advice?',
];

function AssistantTab() {
  const { user } = useAuth();
  const userName = user?.name || 'there';
  const [messages, setMessages] = useState([]);
  const [history, setHistory]   = useState([]);
  const [input, setInput]       = useState('');
  const [typing, setTyping]     = useState(false);
  const [error, setError]       = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const now = new Date();
    const greeting = `Hi ${userName.split(' ')[0]}! 👋 I'm your OptiKa AI assistant. I've analyzed your business data through ${now.toLocaleString('en-US', { month: 'long', year: 'numeric' })}. Ask me anything about your sales, inventory, customers, or finances!`;
    setMessages([{ role: 'assistant', content: greeting }]);
  }, [userName]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  async function send(text) {
    const msg = (text || input).trim();
    if (!msg || typing) return;
    setInput('');
    setError(null);
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setTyping(true);
    try {
      const res = await api.post('/ai/query', { message: msg, conversationHistory: history });
      const reply = res.data.reply || 'Sorry, no response received.';
      setMessages(m => [...m, { role: 'assistant', content: reply }]);
      setHistory(h => [...h, { role: 'user', content: msg }, { role: 'assistant', content: reply }].slice(-10));
    } catch (e) {
      console.error('[AIInsights] query error:', e);
      const reply = e.response?.data?.reply || e.response?.data?.error || 'Connection error. Please try again.';
      setMessages(m => [...m, { role: 'assistant', content: reply }]);
      setError(reply);
    } finally {
      setTyping(false);
    }
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: C.text, margin: 0 }}>AI Business Assistant</h2>
          <p style={{ fontSize: 12, color: C.muted, margin: '2px 0 0' }}>Ask anything about your business</p>
        </div>
        <Badge color={C.teal} bg="rgba(0,201,177,0.12)"
          style={{ marginLeft: 'auto', border: '1px solid rgba(0,201,177,0.3)' }}>● Online</Badge>
      </div>

      <div className="ai-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="ai-chat-area">
          {messages.map((m, i) => (
            <ChatBubble key={i} role={m.role} content={m.content} userName={user?.name} />
          ))}
          {typing && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 14 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(0,201,177,0.2)', border: `1px solid ${C.teal}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800, color: C.teal,
                fontFamily: 'JetBrains Mono, monospace',
              }}>AI</div>
              <div style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`,
                borderRadius: '14px 14px 14px 4px', padding: '10px 14px' }}>
                <TypingDots />
              </div>
            </div>
          )}
          {error && <div style={{ color: C.coral, fontSize: 12, textAlign: 'center', marginBottom: 10 }}>{error}</div>}
          <div ref={chatEndRef} />
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, padding: 14 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="ai-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about your business..."
            />
            <button className="ai-send-btn" onClick={() => send()} disabled={!input.trim() || typing}>
              Send →
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {QUICK_QUESTIONS.map(q => (
              <button key={q} className="ai-quick-btn" onClick={() => send(q)} disabled={typing}>{q}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — FORECASTING
// ─────────────────────────────────────────────────────────────────────────────

function ForecastTab() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg]   = useState(null);

  useEffect(() => {
    api.get('/ai/forecast')
       .then(r => setData(r.data))
       .catch(e => {
         console.error('[AIInsights] forecast load error:', e);
         setErrMsg(e.response?.data?.error || 'Failed to load forecast. Please try again.');
       })
       .finally(() => setLoading(false));
  }, []);

  const animatedRevenue = useAnimatedCounter(data?.forecastRevenue || 0, 1400);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Skeleton h={180} r={16} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <Skeleton h={180} r={14} /><Skeleton h={180} r={14} /><Skeleton h={180} r={14} />
      </div>
    </div>
  );

  if (!data) return <div style={{ color: C.coral, textAlign: 'center', padding: 40 }}>{errMsg || 'Could not load forecast data.'}</div>;

  const confidence = data.confidence || 0;
  const confColor = confidence >= 80 ? C.teal : confidence >= 65 ? C.gold : C.coral;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Hero card */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(0,201,177,0.15) 0%, rgba(56,189,248,0.10) 100%)',
        border: `1px solid rgba(0,201,177,0.25)`,
        borderRadius: 16, padding: '28px 32px',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.teal, letterSpacing: '0.07em', marginBottom: 8, textTransform: 'uppercase' }}>
          {data.month.toUpperCase()} REVENUE FORECAST
        </div>
        <div className="ai-mono" style={{ fontSize: 40, fontWeight: 800, color: C.gold, lineHeight: 1, marginBottom: 6 }}>
          GHS {animatedRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
          Based on {data.basedOn?.map(b => b.period).join(', ')} trends
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: C.muted }}>Forecast Confidence</span>
            <span className="ai-mono" style={{ fontSize: 12, fontWeight: 700, color: confColor }}>{confidence}%</span>
          </div>
          <div style={{ height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 100 }}>
            <div style={{ height: '100%', width: `${confidence}%`, background: confColor, borderRadius: 100, transition: 'width 1.2s ease' }} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[
            { label: 'Low Estimate',  value: formatCurrencyCompact(data.lowEstimate) },
            { label: 'High Estimate', value: formatCurrencyCompact(data.highEstimate) },
            { label: 'vs Last Month', value: `${data.vsLastMonth >= 0 ? '+' : ''}${data.vsLastMonth}%` },
          ].map(s => (
            <div key={s.label} className="ai-stat-mini">
              <div style={{ fontSize: 10, color: C.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
              <div className="ai-mono" style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Three detail cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 14 }}>Top Product Forecasts</div>
          {(data.topProductForecasts || []).slice(0, 3).map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{p.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>Forecast qty: {p.forecastQty}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="ai-mono" style={{ fontSize: 12, fontWeight: 700, color: C.teal }}>{formatCurrencyCompact(p.forecastRevenue)}</div>
                <div style={{ fontSize: 10, color: C.muted }}>was {formatCurrencyCompact(p.lastMonthRevenue)}</div>
              </div>
            </div>
          ))}
          {(!data.topProductForecasts?.length) && <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', padding: '20px 0' }}>No product data</div>}
        </Card>

        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 14 }}>Smart Reorder Plan</div>
          {(data.reorderRecommendations || []).slice(0, 4).map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '7px 0', borderBottom: i < 3 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ fontSize: 12, color: C.text, fontWeight: 500, flex: 1, marginRight: 8 }}>{r.name}</div>
              <Badge color={C.gold} bg="rgba(240,180,41,0.12)">{r.suggestedOrderQty} {r.unit}</Badge>
            </div>
          ))}
          {(!data.reorderRecommendations?.length) && (
            <div style={{ fontSize: 12, color: C.teal, textAlign: 'center', padding: '20px 0' }}>✓ Stock levels look good</div>
          )}
        </Card>

        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 14 }}>Profit Forecast</div>
          <div style={{ textAlign: 'center', padding: '8px 0 14px' }}>
            <div className="ai-mono" style={{ fontSize: 26, fontWeight: 800, color: data.forecastProfit >= 0 ? C.teal : C.coral }}>
              {formatCurrencyCompact(data.forecastProfit)}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Projected net profit</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '10px 14px', border: `1px solid ${C.border}` }}>
            {[
              { label: 'Growth rate',      value: `${data.growthRate >= 0 ? '+' : ''}${data.growthRate}%` },
              { label: 'Forecast revenue', value: formatCurrencyCompact(data.forecastRevenue) },
              { label: 'Status',           value: data.vsLastMonth >= 0 ? 'On track 📈' : 'Watch 📉' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                <span style={{ color: C.muted }}>{r.label}</span>
                <span className="ai-mono" style={{ fontWeight: 600, color: C.text }}>{r.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3 — ANOMALY DETECTION
// ─────────────────────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  high:     { bg: 'rgba(255,107,107,0.10)', border: 'rgba(255,107,107,0.35)', icon: '🚨', badgeBg: 'rgba(255,107,107,0.15)', badgeColor: C.coral },
  medium:   { bg: 'rgba(240,180,41,0.10)',  border: 'rgba(240,180,41,0.35)',  icon: '⚠️', badgeBg: 'rgba(240,180,41,0.15)',  badgeColor: C.gold  },
  low:      { bg: 'rgba(56,189,248,0.10)',  border: 'rgba(56,189,248,0.35)',  icon: 'ℹ️', badgeBg: 'rgba(56,189,248,0.15)',  badgeColor: C.sky   },
  positive: { bg: 'rgba(0,201,177,0.10)',   border: 'rgba(0,201,177,0.35)',   icon: '🎉', badgeBg: 'rgba(0,201,177,0.15)',   badgeColor: C.teal  },
};

function AnomalyCard({ anomaly }) {
  const sty = SEVERITY_STYLES[anomaly.severity] || SEVERITY_STYLES.low;
  return (
    <div className="ai-anomaly-card" style={{ background: sty.bg, borderColor: sty.border }}>
      <div style={{ fontSize: 28, flexShrink: 0, marginTop: 2 }}>{sty.icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{anomaly.title}</span>
          <Badge bg={sty.badgeBg} color={sty.badgeColor}>{anomaly.severity}</Badge>
        </div>
        <p style={{ fontSize: 12, color: C.muted, margin: '0 0 6px', lineHeight: 1.5 }}>{anomaly.description}</p>
        <p style={{ fontSize: 12, fontWeight: 600, color: C.text, margin: 0 }}>
          <span style={{ color: C.gold }}>→ </span>{anomaly.recommendation}
        </p>
      </div>
    </div>
  );
}

function AnomalyTab() {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading]     = useState(true);

  const fetchAnomalies = useCallback(() => {
    setLoading(true);
    api.get('/ai/anomalies')
       .then(r => setAnomalies(r.data))
       .catch(() => setAnomalies([]))
       .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAnomalies(); }, [fetchAnomalies]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>
            Anomaly Detection — Last 30 Days
          </h2>
          {!loading && (
            anomalies.length > 0
              ? <Badge bg="rgba(240,180,41,0.12)" color={C.gold}>{anomalies.length} anomali{anomalies.length === 1 ? 'y' : 'es'} found</Badge>
              : <Badge bg="rgba(0,201,177,0.12)" color={C.teal}>All clear</Badge>
          )}
        </div>
        <button className="ai-refresh-btn" onClick={fetchAnomalies} disabled={loading}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1,2,3].map(i => <Skeleton key={i} h={90} r={12} />)}
        </div>
      ) : anomalies.length === 0 ? (
        <div style={{
          background: 'rgba(0,201,177,0.08)', border: '1px solid rgba(0,201,177,0.25)',
          borderRadius: 14, padding: '32px 24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.teal, marginBottom: 6 }}>No anomalies detected in the last 30 days</div>
          <div style={{ fontSize: 13, color: C.muted }}>Your business data looks clean and consistent!</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {anomalies.map((a, i) => <AnomalyCard key={i} anomaly={a} />)}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 4 — BUSINESS HEALTH
// ─────────────────────────────────────────────────────────────────────────────

const PRIORITY_STYLES = {
  high:   { bg: 'rgba(0,201,177,0.08)',   border: 'rgba(0,201,177,0.25)',   color: C.teal    },
  medium: { bg: 'rgba(240,180,41,0.08)',  border: 'rgba(240,180,41,0.25)',  color: C.gold    },
  low:    { bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.25)', color: C.lavender},
};

function HealthTab() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg]   = useState(null);

  useEffect(() => {
    api.get('/ai/health-score')
       .then(r => setData(r.data))
       .catch(e => {
         console.error('[AIInsights] health score load error:', e);
         setErrMsg(e.response?.data?.error || 'Failed to load health score. Please try again.');
       })
       .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Skeleton h={300} r={14} /><Skeleton h={300} r={14} />
      </div>
      <Skeleton h={200} r={14} />
    </div>
  );

  if (!data) return <div style={{ color: C.coral, textAlign: 'center', padding: 40 }}>{errMsg || 'Could not load health score.'}</div>;

  if (data.isEmpty) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card style={{ textAlign: 'center', padding: '48px 32px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <h3 style={{ color: C.gold, marginBottom: 8 }}>No Data Yet</h3>
          <p style={{ color: C.muted, fontSize: 14, marginBottom: 24 }}>
            Add products and record sales to see your business health score.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320, margin: '0 auto' }}>
            {data.recommendations.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12,
                background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`,
                borderRadius: 10, padding: '12px 16px', textAlign: 'left' }}>
                <span style={{ fontSize: 24 }}>{r.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{r.title}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{r.description}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  const scoreColor = data.overallScore === 0 ? C.muted : data.overallScore >= 90 ? C.teal : data.overallScore >= 76 ? '#16A34A' : data.overallScore >= 61 ? C.sky : data.overallScore >= 46 ? C.gold : C.coral;
  const labelColor = data.overallScore === 0 ? C.muted : data.overallScore >= 76 ? C.teal : data.overallScore >= 61 ? C.gold : data.overallScore >= 46 ? C.gold : C.coral;
  const labelBg    = data.overallScore === 0 ? 'rgba(255,255,255,0.05)' : data.overallScore >= 76 ? 'rgba(0,201,177,0.12)' : data.overallScore >= 61 ? 'rgba(240,180,41,0.12)' : data.overallScore >= 46 ? 'rgba(240,180,41,0.12)' : 'rgba(255,107,107,0.12)';

  const dimLabels = {
    revenueGrowth:     'Revenue Growth',
    profitMargin:      'Profit Margin',
    inventoryHealth:   'Inventory Health',
    customerRetention: 'Customer Retention',
    cashFlow:          'Cash Flow',
    expenseControl:    'Expense Control',
    debtRecovery:      'Debt Recovery',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Score card */}
        <Card style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 20 }}>Business Health Score</div>
          <ScoreCircle score={data.overallScore} size={160} />
          <div style={{ marginTop: 16 }}>
            <Badge bg={labelBg} color={labelColor} style={{ fontSize: 13, padding: '5px 16px' }}>{data.label}</Badge>
          </div>
          <p style={{ fontSize: 12, color: C.muted, marginTop: 12, lineHeight: 1.5 }}>
            {data.overallScore >= 75
              ? 'Your business is performing well. Keep up the momentum!'
              : data.overallScore >= 60
              ? 'Your business is healthy. A few areas need attention to unlock growth.'
              : data.overallScore >= 45
              ? 'Some key metrics need improvement. Review recommendations below.'
              : 'Your business needs urgent attention on multiple fronts.'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
            {[
              { label: 'Net Margin',        value: `${data.meta?.netMargin ?? '—'}%` },
              { label: 'Growth Rate',       value: `${data.meta?.growthRate >= 0 ? '+' : ''}${data.meta?.growthRate ?? '—'}%` },
              { label: 'Active Customers',  value: `${data.meta?.activeCustomers ?? '—'}` },
              { label: 'Month',             value: data.meta?.currentMonth?.split(' ')[0] || '—' },
            ].map(s => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 10px', textAlign: 'center' }}>
                <div className="ai-mono" style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{s.value}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{s.label}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Score breakdown */}
        <Card>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 18 }}>Score Breakdown</div>
          {Object.entries(data.dimensions || {}).map(([key, score]) => (
            <DimBar key={key} label={dimLabels[key] || key} score={score} />
          ))}
        </Card>
      </div>

      {/* AI Recommendations */}
      {(data.recommendations || []).length > 0 && (
        <Card>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 16 }}>🤖 AI Recommendations</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            {data.recommendations.map((rec, i) => {
              const sty = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.low;
              return (
                <div key={i} className="ai-rec-card" style={{ background: sty.bg, borderColor: sty.border }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>{rec.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{rec.title}</span>
                  </div>
                  <p style={{ fontSize: 12, color: C.muted, margin: 0, lineHeight: 1.55 }}>{rec.description}</p>
                  <div style={{ marginTop: 8 }}>
                    <Badge bg={sty.bg} color={sty.color} style={{ border: `1px solid ${sty.border}` }}>
                      {rec.priority} priority
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function AIInsights() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'assistant';

  return (
    <Layout>
      <PageTransition>
        <style>{STYLES}</style>

        <div className="ai-page">
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>

            {/* Header */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
                <h1 style={{ fontSize: 26, fontWeight: 800, color: C.text, margin: 0, lineHeight: 1 }}>AI Insights</h1>
                <span className="ai-serif" style={{ fontSize: 20, color: C.gold }}>intelligence</span>
              </div>
              <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>
                AI-powered analytics, forecasting and business intelligence
              </p>
            </div>

            {/* Tab bar */}
            <div style={{
              display: 'flex', background: C.surface,
              borderRadius: '12px 12px 0 0',
              borderBottom: `2px solid ${C.border}`,
              marginBottom: 24, overflowX: 'auto',
            }}>
              {TABS.map(t => (
                <button key={t.key}
                  className={`ai-tab-btn${tab === t.key ? ' active' : ''}`}
                  onClick={() => setSearchParams({ tab: t.key })}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'assistant' && <AssistantTab />}
            {tab === 'forecast'  && <ForecastTab />}
            {tab === 'anomalies' && <AnomalyTab />}
            {tab === 'health'    && <HealthTab />}
          </div>
        </div>
      </PageTransition>
    </Layout>
  );
}
